import React from "react";

// Material Imports
import { Paper, Button, Tooltip, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Import CSS
import "components/pages/transmissions/modal/sign/SignModal.css";
import LoadingSpinner from "components/LoadingSpinner";
import {
  setCacheRefresh,
  toggleModal,
  toggleProcessing,
  toggleReportSigning,
  toggleFetchTableData,
  selectedReport,
} from "app/store/transmission/slice/transmissionSlice";
import { useSelector, useDispatch } from "react-redux";
import {
  useSignReportsMutation,
  useUnsignReportsMutation,
} from "app/store/transmission/api/transmissionTableApi";
import { removeCache } from "app/utils/cache";
import { showErrorToast, showSuccessToast } from "utils/toast";

const SignModal = ({ setRowSelection }) => {
  const {
    totalSelectedRows,
    isSigningModal,
    isProcessing,
    selectedReports,
    signingModalState,
    isReportUnsigning,
    reportId,
    reportDetail,
    isLegacyReport,
  } = useSelector((state) => state.transmission);

  const isSign = signingModalState === "sign" ? true : false;

  const dispatch = useDispatch();
  const [signReports] = useSignReportsMutation();
  const [unsignReports] = useUnsignReportsMutation();

  // Determine which action to take based on `isSign`
  const actionReports = isSign ? signReports : unsignReports;

  const onConfirm = async () => {
    try {
      dispatch(toggleProcessing(true));
      const reportsToUpdate = reportId ? [reportId] : selectedReports;
      const response = await actionReports(reportsToUpdate);
      const errorMessage = response?.error?.message;

      if (!errorMessage) {
        showSuccessToast(response?.data?.message);
        dispatch(toggleModal({ type: signingModalState, value: false }));
        setRowSelection?.({});

        if (!isSign && reportId) {
          await dispatch(
            selectedReport({
              id: reportId,
              isLegacyReport: isLegacyReport,
              reportDetail: {
                ...reportDetail,
                eSig: false,
                signedBy: null,
                signed_at: null,
              },
            })
          );
        }
      } else {
        showErrorToast(errorMessage);
      }

      await dispatch(toggleFetchTableData(true));
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      dispatch(toggleProcessing(false));
      dispatch(toggleReportSigning({ type: signingModalState, value: false }));
      dispatch(toggleModal({ type: signingModalState, value: false }));
      await removeCache();
      await dispatch(setCacheRefresh(false));
      await dispatch(toggleFetchTableData(false));
    }
  };

  const onClose = () => {
    dispatch(toggleProcessing(false));
    dispatch(toggleModal({ type: signingModalState, value: false }));
    dispatch(toggleReportSigning({ type: signingModalState, value: false }));
  };

  return (
    <div>
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isSigningModal}
        onClose={onClose}
      >
        <div className="process-modal__container">
          <Paper>
            <div className="process-modal__wrapper">
              <Tooltip title="Close" placement="bottom">
                <div onClick={onClose} className="process-modal__close">
                  <CloseIcon />
                </div>
              </Tooltip>
              <div className="process-modal__text">
                {!isProcessing && (
                  <div>
                    {!reportId && totalSelectedRows === 1 && (
                      <div>
                        Are you sure you want to {isSign ? "sign" : "unsign"}{" "}
                        this record?
                      </div>
                    )}
                    {!reportId && totalSelectedRows > 1 && (
                      <div>
                        Are you sure you want to {isSign ? "sign" : "unsign"}{" "}
                        these {totalSelectedRows} records?
                      </div>
                    )}
                    {reportId && isReportUnsigning && (
                      <div>
                        Are you sure you want to {isSign ? "sign" : "unsign"}{" "}
                        this record?
                      </div>
                    )}
                  </div>
                )}
                {isProcessing && (
                  <div>
                    <div className="process-modal__processing">
                      {isSign ? "Signing" : "Unsigning"}{" "}
                      {reportId ? 1 : totalSelectedRows}{" "}
                      {totalSelectedRows > 1 ? "reports" : "report"}.
                    </div>
                    <div className="process-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                )}
              </div>
              <div className="process-modal__buttons">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onConfirm}
                  disabled={isProcessing}
                >
                  {isSign ? "Sign" : "Unsign"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </Modal>
    </div>
  );
};

export default SignModal;
